import { Box, Typography, useTheme } from "@mui/material";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "Navigation/useNavigate";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { Resources, useResource } from "Translations/Resources";
import { useCampaignTransactionInitiateMutation } from "Api/Mutations/Transactions/useCampaignTransactionInitiateMutation";
import { buildCampaignTransactionUrl } from "Components/Campaigns/utils";
import {
  type DonationFormData,
  getDonationValidationSchema,
} from "Components/Campaigns/schema";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import type { CurrencyCode } from "Infrastructure/Api/Api";
import { darkenColor } from "Utils/ColorUtils";
import { getCurrencySymbol } from "Utils/CurrencyUtils";

const RESOURCES = Resources.Campaigns.Campaign.DonationForm;

type Props = {
  affiliateCode?: string | null;
  campaignCode: string;
  color?: string | null;
  // The campaign's market currency (the receiver's region decides it — an SK campaign
  // collects EUR). Null only in the admin preview of a draft without a receiver; the
  // input then shows no symbol rather than a guessed one.
  currencyCode?: CurrencyCode | null;
};

export const DonationForm = ({
  affiliateCode,
  campaignCode,
  color: propsColor,
  currencyCode,
}: Props) => {
  const navigate = useNavigate();
  const { t } = useResource();
  const theme = useTheme();

  const currencySymbol = currencyCode ? getCurrencySymbol(currencyCode) : "";

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = useForm<DonationFormData>({
    resolver: zodResolver(getDonationValidationSchema(t, currencySymbol)),
  });

  const { mutate: transactionInitiateMutation, isPending } =
    useCampaignTransactionInitiateMutation({
      onSuccess: ({ transaction }) => {
        const url = buildCampaignTransactionUrl(
          transaction.publicID,
          campaignCode,
          affiliateCode,
        );
        navigate(url);
      },
    });

  const color = propsColor ?? theme.palette.primary.main;

  const onSubmit = async ({ amount }: DonationFormData) => {
    transactionInitiateMutation({ amount, campaignCode });
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} width="100%">
      <Box mb={3}>
        <FormInput
          control={control}
          name="amount"
          label={t(RESOURCES.AmountLabel)}
          fullWidth
          placeholder="100"
          disabled={isSubmitting}
          inputEndAdornment={
            currencySymbol ? (
              <Typography>{currencySymbol}</Typography>
            ) : undefined
          }
          disableHelperText
          disablePadding
          mask={Number}
          scale={2}
          isNumber
        />
      </Box>
      <PrimaryButton
        type="submit"
        fullWidth
        variant="contained"
        color="primary"
        isLoading={isPending}
        sx={{
          fontWeight: "bold",
          backgroundColor: color,
          "&:hover": {
            backgroundColor: darkenColor(color, 15),
          },
          "&:active": {
            backgroundColor: color,
          },
          "&:focus": {
            backgroundColor: color,
          },
        }}
      >
        {t(RESOURCES.SubmitButton)}
      </PrimaryButton>
    </Box>
  );
};
